import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Layout from './Layout';
import { Typography, Box, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, Pagination } from '@mui/material';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import truncateText from './utils/truncateText';

function HomePage() {
  const location = useLocation();
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const userId = localStorage.getItem('user_id');
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState('');
  const [billSessions, setBillSessions] = useState([]);
  const params = new URLSearchParams(location.search);
  const searchType = params.get('type') || 'basic';
  const [searchQuery, setSearchQuery] = useState('');
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const fetchBillSessions = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/bills/state-sessions/${userId}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${process.env.REACT_APP_API_TOKEN}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch bill sessions');
        }

        const data = await response.json();
        setBillSessions(data.sessions || []);
        if (data.sessions && data.sessions.length > 0) {
          setSelectedSession(data.sessions[0].id);
        }
      } catch (error) {
        console.error('Error fetching bill sessions:', error);
      }
    };

    fetchBillSessions();
  }, [userId]);

  const showTruncatedText = (text, limit) => {
    const truncated = truncateText(text, limit);
    return text.length > truncated.length;
  };

  const handleExport = async (page) => {
    setLoading(true);
    try {      
      const query = params.get('q') || '';
      const topicQuery = params.get('tq') || '';
      
      const tracked = params.get('tracked');
      setSearchQuery(query || '');

      let searchStr = ""
      if (searchType === 'topic') {
        searchStr = topicQuery; 
      } else {
        searchStr = query;
      }

      let apiUrl = `${process.env.REACT_APP_API_URL}/bills/export/${userId}/?q=${searchStr}&bill_session=${selectedSession}`;

      if (tracked) {
        apiUrl += `&tracked=${tracked}`;
      }

      const response = await fetch(apiUrl, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${process.env.REACT_APP_API_TOKEN}`,
        },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.text();
      const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'export.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Error fetching search results:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch bills with pagination
  const handleSearch = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const params = new URLSearchParams(location.search);
      const query = params.get('q') || '';
      const topicQuery = params.get('tq') || '';
      const tracked = params.get('tracked');

      let searchStr = "";
      if (searchType === 'topic') {
        searchStr = topicQuery; 
      } else {
        searchStr = query;
      }

      setSearchQuery(query || '');
      // setPageSize(params.get('pageSize') || 20);

      let apiUrl = `${process.env.REACT_APP_API_URL}/bills/search/${userId}/?q=${searchStr}&page=${page}&type=${searchType}&bill_session=${selectedSession}&items_per_page=${pageSize}`;

      if (tracked) {
        apiUrl += `&tracked=${tracked}`;
      }

      const response = await fetch(apiUrl, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${process.env.REACT_APP_API_TOKEN}`,
        },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      // setPageSize(data.items_per_page || 20);
      setBills(data.bills || []);
      setCurrentPage(data.page || 1);
      // Calculate totalPages from total_count and items_per_page
      setTotalCount(data.total);
      setTotalPages(Math.ceil(data.total / pageSize) || 1);
    } catch (error) {
      console.error('Error fetching search results:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleSearch(currentPage, pageSize);
    // eslint-disable-next-line
  }, [location.search, currentPage, selectedSession, pageSize]);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    // Do NOT call handleSearch here!
  };

  const handleBookmarkClick = async (billId) => {
    try {
      const billIndex = bills.findIndex(bill => bill.id === billId);
      if (billIndex === -1) {
        throw new Error('Bill not found');
      }

      const isBookmarked = bills[billIndex].tracking === 1;
      const action = isBookmarked ? 'delete' : 'add';

      const response = await fetch(`${process.env.REACT_APP_API_URL}/save-user-bill/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${process.env.REACT_APP_API_TOKEN}`,
        },
        body: JSON.stringify({ bill_id: billId, user_id: userId, action: action }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      console.log(`Bill ${action}ed successfully:`, data);

      // Update the bill's tracking status in the state
      const updatedBills = [...bills];
      updatedBills[billIndex] = {
        ...updatedBills[billIndex],
        tracking: isBookmarked ? 0 : 1,
      };
      setBills(updatedBills);
    } catch (error) {
      console.error(error);
    }
  };

  const handlePageSizeChange = (event) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page
    handleSearch(1, newSize);
  };

  return (
    <Layout searchQuery={searchQuery}>
      <Box
        sx={{
          overflowY: 'auto',
          padding: 2,
          width: '100%',
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
            {searchType === 'topic' && (
              <Typography variant="body2" color="textSecondary" mt={2}>
                Give us a couple second...this takes longer than a basic search.
              </Typography>
            )}
          </Box>
        ) : (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setFilterDialogOpen(true)}
              >
                Filter
              </Button>
              {bills.length ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleExport(currentPage)}
                  disabled={loading}
                >
                  Export to Excel
                </Button>
              ) : null}
            </Box>

            <Dialog open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)}>
              <DialogTitle>Filter Bills</DialogTitle>
              <DialogContent>
                <FormControl fullWidth>
                <b>Bill Session</b>
                <Select
                    value={selectedSession}
                    onChange={(e) => setSelectedSession(e.target.value)}
                  >
                    {billSessions.map((session) => (
                      <MenuItem key={session.id} value={session.id}>
                        {session.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setFilterDialogOpen(false)} color="primary">
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setFilterDialogOpen(false);
                    handleSearch(1, selectedSession);
                  }}
                  color="primary"
                >
                  Apply
                </Button>
              </DialogActions>
            </Dialog>

            {bills.length === 0 && (
              <Typography>No results found</Typography>
            )}

            {bills.map(bill => (
              <React.Fragment key={bill.id}>
                <Box
                  sx={{
                    backgroundColor: '#F4F4F4',
                    borderRadius: 2,
                    padding: 2,
                    maxWidth: '90%',
                    marginBottom: 2,
                    fontFamily: 'Lato',
                  }}
                >
                  <Link
                    to={`/bill/${bill.id}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ textDecoration: 'none', color: 'inherit' }}
                    onClick={() => window.scrollTo(0, 0)}
                  >
                    <Typography>
                      {bill.tracking === 1 ? (
                        <BookmarkIcon
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            handleBookmarkClick(bill.id);
                          }}
                          style={{ verticalAlign: 'middle', marginRight: 8, cursor: 'pointer' }}
                        />
                      ) : (
                        <BookmarkBorderIcon
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            handleBookmarkClick(bill.id);
                          }}
                          style={{ verticalAlign: 'middle', marginRight: 8, cursor: 'pointer' }}
                        />
                      )}

                      {`${bill.year_introduced} | ${bill.state} ${bill.bill_number} ${bill.title ? ` - ${bill.title}` : ''}`}

                      {bill.policy_objective && bill.policy_objective.length > 0 ? (
                        <Typography sx={{ marginTop: 1 }}>
                          {showTruncatedText(bill.policy_objective, 45) ? (
                            <>
                              {bill.showPolicyObjective ? (
                                <>
                                  {bill.policy_objective}
                                  <Button
                                    variant="text"
                                    size="small"
                                    startIcon={<ExpandLessIcon />}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const updatedBills = bills.map(b =>
                                        b.id === bill.id ? { ...b, showPolicyObjective: false } : b
                                      );
                                      setBills(updatedBills);
                                    }}
                                  />
                                </>
                              ) : (
                                <>
                                  {truncateText(bill.policy_objective, 45)}
                                  <Button
                                    variant="text"
                                    size="small"
                                    startIcon={<ExpandMoreIcon />}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const updatedBills = bills.map(b =>
                                        b.id === bill.id ? { ...b, showPolicyObjective: true } : b
                                      );
                                      setBills(updatedBills);
                                    }}
                                  />
                                </>
                              )}
                            </>
                          ) : (
                            bill.policy_objective
                          )}
                        </Typography>
                      ) : bill.description && bill.description.length > 0 ? (
                        <Typography sx={{ marginTop: 1 }}>
                          {showTruncatedText(bill.description, 45) ? (
                            <>
                              {bill.showFullDescription ? (
                                <>
                                  {bill.description}
                                  <Button
                                    variant="text"
                                    size="small"
                                    startIcon={<ExpandLessIcon />}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const updatedBills = bills.map(b =>
                                        b.id === bill.id ? { ...b, showFullDescription: false } : b
                                      );
                                      setBills(updatedBills);
                                    }}
                                  />
                                </>
                              ) : (
                                <>
                                  {truncateText(bill.description, 45)}
                                  <Button
                                    variant="text"
                                    size="small"
                                    startIcon={<ExpandMoreIcon />}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const updatedBills = bills.map(b =>
                                        b.id === bill.id ? { ...b, showFullDescription: true } : b
                                      );
                                      setBills(updatedBills);
                                    }}
                                  />
                                </>
                              )}
                            </>
                          ) : (
                            bill.description
                          )}
                        </Typography>
                      ) : null}

                      {bill.status && (
                        <Typography sx={{ marginTop: 1 }}>
                          <b>Status:</b> {bill.status}
                        </Typography>
                      )}

                      {bill.controversy_level && (
                        <Box sx={{ display: 'flex', alignItems: 'center', marginTop: 1 }}>
                          <Typography sx={{ marginRight: 1 }}>
                            <b>Controversy:</b>
                          </Typography>
                          {Array.from({ length: 5 }).map((_, index) => (
                            <img
                              key={index}
                              src={index < Math.floor(bill.controversy_level / 2) ? '/images/fire-on.svg' : '/images/fire-off.svg'}
                              alt="Fire"
                              style={{ width: 20, height: 20, marginRight: 4 }}
                            />
                          ))}
                        </Box>
                      )}

                      {bill.sponsors && bill.sponsors.length > 0 && (
                        <Typography sx={{ marginTop: 1 }}>
                          <b>Sponsor(s):</b>
                          {bill.showAllSponsors
                            ? bill.sponsors.map(sponsor => sponsor.name).join(', ')
                            : bill.sponsors.slice(0, 2).map(sponsor => sponsor.name).join(', ')}
                          {bill.sponsors.length > 2 && (
                            <>
                              {bill.showAllSponsors ? (
                                <Button
                                  variant="text"
                                  size="small"
                                  startIcon={<ExpandLessIcon />}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const updatedBills = bills.map(b =>
                                      b.id === bill.id ? { ...b, showAllSponsors: false } : b
                                    );
                                    setBills(updatedBills);
                                  }}
                                />
                              ) : (
                                <Button
                                  variant="text"
                                  size="small"
                                  startIcon={<ExpandMoreIcon />}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const updatedBills = bills.map(b =>
                                      b.id === bill.id ? { ...b, showAllSponsors: true } : b
                                    );
                                    setBills(updatedBills);
                                  }}
                                />
                              )}
                            </>
                          )}
                        </Typography>
                      )}

                      {bill.category && bill.category.length > 0 && (
                          <Typography sx={{ marginTop: 1 }}>
                            <b>AI Category:</b> {bill.political_category}
                          </Typography>
                        )}

                      {bill.labels && bill.labels.length > 0 && (
                        <Box sx={{ marginTop: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          <b>Label(s):</b> 
                          {bill.labels.map((label, index) => {
                            const isDark = index % 2 === 0;
                            const backgroundColor = isDark ? '#0B7269' : '#CCFBF1';
                            const textColor = isDark ? '#FFFFFF' : '#0B7269';

                            return (
                              <Box
                                key={index}
                                sx={{
                                  backgroundColor,
                                  color: textColor,
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '0.875rem',
                                  fontWeight: 'bold',
                                }}
                              >
                                {label}
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Typography>
                  </Link>
                </Box>
                <Box sx={{ height: 16 }} /> {/* Spacer element */}
              </React.Fragment>
            ))}

            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
              <FormControl size="small" variant="outlined" sx={{ minWidth: 120 }}>
                <InputLabel id="page-size-label">Rows per page</InputLabel>
                <Select
                  labelId="page-size-label"
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  label="Rows per page"
                >
                  {[10, 20, 50, 100].map((size) => (
                    <MenuItem key={size} value={size}>{size}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          </>
        )}
      </Box>
    </Layout>
  );
}

export default HomePage;